import { useDisclosure } from "@mantine/hooks";
import type { PropsWithChildren } from "react";
import { t } from "ttag";

import { useUniqueId } from "metabase/common/hooks/use-unique-id";
import CS from "metabase/css/core/index.css";
import { Box, Collapse, Group, Icon, UnstyledButton } from "metabase/ui";

import { PaddedSidebarLink, SidebarHeading } from "../MainNavbar.styled";
import { trackOnboardingChecklistOpened } from "../analytics";
import type { SelectedItem } from "../types";

import { useCanAddData } from "./use-can-add-data";

export const GettingStartedSection = ({
  nonEntityItem,
  onAddDataModalOpen,
  children,
}: PropsWithChildren<{
  nonEntityItem: SelectedItem;
  onAddDataModalOpen: () => void;
}>) => {
  const canAddData = useCanAddData();
  const [opened, { toggle }] = useDisclosure(true);
  const buttonId = useUniqueId();
  const panelId = useUniqueId();

  const ONBOARDING_URL = "/getting-started";
  const isOnboardingPageSelected = nonEntityItem?.url === ONBOARDING_URL;

  return (
    <div>
      <Group
        id={buttonId}
        align="center"
        gap="sm"
        onClick={toggle}
        component={UnstyledButton}
        aria-expanded={opened}
        aria-controls={panelId}
        c="text-secondary"
        mb="sm"
        className={CS.cursorPointer}
      >
        <SidebarHeading>{t`Getting Started`}</SidebarHeading>
        <Icon
          name={opened ? "chevrondown" : "chevronright"}
          size={8}
          aria-hidden
        />
      </Group>

      <Collapse
        id={panelId}
        in={opened}
        transitionDuration={0}
        role="region"
        aria-labelledby={buttonId}
      >
        {/* A real list, not bare <li>s — WCAG 1.3.1, list_structure_proper.
            `children` (the Examples tree) stays a sibling: it's already its
            own <ul role="tree">, and nesting a <ul> inside this one would
            trade one structural defect for another. */}
        <Box component="ul">
          {canAddData && (
            <PaddedSidebarLink icon="add_data" onClick={onAddDataModalOpen}>
              {t`Add your data`}
            </PaddedSidebarLink>
          )}

          <PaddedSidebarLink
            icon="learn"
            url={ONBOARDING_URL}
            isSelected={isOnboardingPageSelected}
            onClick={() => trackOnboardingChecklistOpened()}
          >
            {/* eslint-disable-next-line metabase/no-literal-metabase-strings -- We only show this to non-whitelabelled instances */}
            {t`How to use Metabase`}
          </PaddedSidebarLink>
        </Box>

        {children}
      </Collapse>
    </div>
  );
};
